/*  ========================================================================
    # Sections - Benefits Filter
    ========================================================================  */

/* eslint-disable prettier/prettier */

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import useApiSearchPublic from "../../hooks/useApiSearchPublic";
import useLocalization from "../../hooks/useLocalization";
import { BenefitCard, BenefitCardSkeleton } from "../../components/BenefitCard";

const DistanceOptions = [
  { value: 1, label: "Ganzer Ort" },
  { value: 5, label: "+ 5 km" },
  { value: 10, label: "+ 10 km" },
  { value: 20, label: "+ 20 km" },
  { value: 50, label: "+ 50 km" },
  { value: 100, label: "+ 100 km" },
];

const BenefitsFilter = ({ categories, baseUrl }) => {
  const localize = useLocalization();
  const benefitsContainer = document.querySelector(".s-benefits .container");
  const [loadingUserLocation, setLoadingUserLocation] = useState(false);
  const [showEnterSomethingMsg, setShowEnterSomethingMsg] = useState(false);

  // Categories
  const [categoriesDropdownOpen, setCategoriesDropdownOpen] = useState(false);
  const listRef = useRef(null);
  const [categoryFocusIndex, setCategoryFocusIndex] = useState(-1);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoriesParam, setCategoriesParam] = useState([]);

  // Location
  const locationInputRef = useRef(null);
  const distanceListRef = useRef(null);
  const [distanceFocusIndex, setDistanceFocusIndex] = useState(-1);
  const [locationValue, setLocationValue] = useState("");
  const [locationParam, setLocationParam] = useState("");

  // Distance
  const [distanceDropdownOpen, setDistanceDropdownOpen] = useState(false);
  const [selectedDistance, setSelectedDistance] = useState(1);
  const [distanceParam, setDistanceParam] = useState(null);

  /**
   * Handle backdrop.
   */
  const handleBackdrop = () => {
    if (categoriesDropdownOpen) {
      setCategoriesDropdownOpen(false);
      setCategoriesParam(selectedCategories);
      setLocationParam(locationValue);
      setDistanceParam(selectedDistance);
    }
    if (distanceDropdownOpen) setDistanceDropdownOpen(false);
  };

  /**
   * Handle categories.
   */

  const setCategoriesDropdown = (open) => {
    setCategoriesDropdownOpen(open);
    setCategoryFocusIndex(open ? 0 : -1);
    if (!open) {
      setCategoriesParam(selectedCategories);
      setLocationParam(locationValue);
      setDistanceParam(selectedDistance);
    }
  };
  const toggleCategoriesDropdown = () => {
    setCategoriesDropdown(!categoriesDropdownOpen);
  };

  const onDropdownKeyDown = (event) => {
    let flag = false;
    switch (event.key) {
      case " ":
      case "Enter":
        if (categoriesDropdownOpen) {
          // Toggle the currently focused option
          handleCategorySelect(
            {
              target: {
                checked: !selectedCategories.includes(
                  categories[categoryFocusIndex],
                ),
              },
            },
            categories[categoryFocusIndex],
          );
        } else {
          // Open the dropdown
          setCategoriesDropdown(true);
        }
        flag = true;
        break;
      case "ArrowDown":
      case "Down":
        if (categoriesDropdownOpen) {
          // Move Focus further down
          setCategoryFocusIndex((prev) =>
            Math.min(prev + 1, categories.length - 1),
          );
        } else {
          setCategoriesDropdown(true);
        }
        flag = true;
        break;
      case "ArrowUp":
      case "Up":
        if (categoriesDropdownOpen) {
          // Move Focus further up
          setCategoryFocusIndex((prev) => Math.max(prev - 1, 0));
          if (categoryFocusIndex === 0) {
            setCategoriesDropdown(false);
          }
        }
        flag = true;
        break;
      case "Esc":
      case "Escape":
        setCategoriesDropdown(false);
        flag = true;
        break;
      default:
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  const onDropdownBlur = (event) => {
    // do nothing if relatedTarget is contained within listboxEl
    if (listRef.current.contains(event.relatedTarget)) {
      return;
    }
    setCategoriesDropdown(false);
  };

  const handleCategorySelect = (event, category) => {
    if (event.target.checked) {
      setSelectedCategories((prev) => [...prev, category]);
    } else {
      setSelectedCategories((prev) => {
        prev.splice(prev.indexOf(category), 1);
        return [...prev];
      });
    }
  };

  /**
   * Handle location input.
   */
  const handleLocationInputOnClick = () => {
    setCategoriesDropdown(false);
    setDistanceDropdown(false);
  };
  const handleLocationInputOnChange = (event) => {
    setLocationValue(event.target.value);

    if (locationValue === "") {
      setLocationParam("");
    }
  };
  const clearLocationInput = () => {
    locationInputRef.current.value = "";
    setLocationValue("");
    setLocationParam("");
    setSelectedDistance(1);
  };

  /**
   * Handle distance.
   */
  const setDistanceDropdown = (open) => {
    setDistanceDropdownOpen(open);
    setDistanceFocusIndex(
      open
        ? selectedDistance
          ? DistanceOptions.findIndex((o) => o.value === selectedDistance)
          : 0
        : -1,
    );
  };
  const toggleDistanceDropdown = () => {
    setDistanceDropdown(!distanceDropdownOpen);
  };
  const handleDistanceSelect = (distance) => {
    setDistanceDropdown(false);
    setSelectedDistance(distance);
  };
  const onDistanceDropdownBlur = (event) => {
    // do nothing if relatedTarget is contained within listboxEl
    if (distanceListRef.current.contains(event.relatedTarget)) {
      return;
    }
    setDistanceDropdown(false);
  };
  const onDistanceDropdownKeyDown = (event) => {
    let flag = false;
    switch (event.key) {
      case " ":
      case "Enter":
        if (distanceDropdownOpen) {
          // Set current option and close
          handleDistanceSelect(DistanceOptions[distanceFocusIndex].value);
          setDistanceDropdown(false);
        } else {
          // Open the dropdown
          setDistanceDropdown(true);
        }
        flag = true;
        break;
      case "ArrowDown":
      case "Down":
        if (distanceDropdownOpen) {
          // Move Focus further down
          setDistanceFocusIndex((prev) =>
            Math.min(prev + 1, DistanceOptions.length - 1),
          );
        } else {
          setDistanceDropdown(true);
        }
        flag = true;
        break;
      case "ArrowUp":
      case "Up":
        if (distanceDropdownOpen) {
          // Move Focus further up
          setDistanceFocusIndex((prev) => Math.max(prev - 1, 0));
          if (distanceFocusIndex === 0) {
            setDistanceDropdown(false);
          }
        }
        flag = true;
        break;
      case "Esc":
      case "Escape":
        setDistanceDropdown(false);
        flag = true;
        break;
      default:
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  /**
   * Get user location.
   */
  const getUserLocation = () => {
    setLoadingUserLocation(true);
    navigator.geolocation.getCurrentPosition((position) => {
      setLoadingUserLocation(false);
      locationInputRef.current.value = "Dein Standort";
      setLocationValue(
        `${position.coords.latitude},${position.coords.longitude}`,
      );
      if (selectedDistance < 5) setSelectedDistance(5);

      setCategoriesParam(selectedCategories);
      setLocationParam(
        `${position.coords.latitude},${position.coords.longitude}`,
      );
      selectedDistance < 5
        ? setDistanceParam(5)
        : setDistanceParam(selectedDistance);
    });
  };

  /**
   * Handle search.
   */
  const handleSearch = () => {
    if (selectedCategories.length === 0 && locationValue === "") {
      setShowEnterSomethingMsg(true);
      return;
    }

    setShowEnterSomethingMsg(false);
    setCategoriesParam(selectedCategories);
    setLocationParam(locationValue);
    setDistanceParam(selectedDistance);
  };

  /**
   * Use public API Search.
   */
  const { loadingSearchResults, errorLoadingSearchResults, searchResults } =
    useApiSearchPublic(categoriesParam, locationParam, distanceParam);

  /**
   * Hide or show benefits container depending on current state.
   */
  useEffect(() => {
    if (selectedCategories.length > 0 || locationParam !== "") {
      benefitsContainer.style.display = "none";
    } else {
      benefitsContainer.style.display = "block";
    }
  }, [categoriesParam, locationParam]);

  return (
    <div className="container">
      <form
        className="s-benefits-filter__form"
        onSubmit={(event) => {
          event.preventDefault();
          handleSearch();
        }}
      >
        <div
          className={`c-benefits-dropdown__backdrop ${categoriesDropdownOpen || distanceDropdownOpen ? "c-benefits-dropdown__backdrop--active" : ""}`}
          onClick={() => handleBackdrop()}
        ></div>

        {/* Categories */}
        <div
          className={`c-benefits-dropdown ${categoriesDropdownOpen ? "c-benefits-dropdown--open" : ""}`}
        >
          <div
            className="c-benefits-dropdown__header"
            id="c-benefits-dropdown__header"
            onClick={toggleCategoriesDropdown}
            onKeyDown={onDropdownKeyDown}
            onBlur={onDropdownBlur}
            tabIndex={0}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={categoriesDropdownOpen}
            aria-controls="c-benefits-dropdown__select"
            aria-label="Kategorien auswählen"
            aria-activedescendant={
              categoryFocusIndex > -1
                ? `c-benefits-dropdown__item--${categoryFocusIndex}`
                : ""
            }
          >
            <span style={{ paddingRight: "28px" }}>
              {selectedCategories.length > 0
                ? selectedCategories.map(
                    (category, index) =>
                      `${index > 0 ? ", " : ""}${category.label}`,
                  )
                : "Kategorien auswählen"}
            </span>
            <svg
              className="c-benefits-dropdown__chevron-down"
              viewBox="0 0 200 200"
              aria-hidden="true"
            >
              <use href={`${localize("svgSprite")}#chevron-down`} />
            </svg>
          </div>
          <div
            ref={listRef}
            className="c-benefits-dropdown__select"
            role="listbox"
            aria-multiselectable="true"
            id="c-benefits-dropdown__select"
            aria-labelledby="c-benefits-dropdown__header"
            tabIndex={-1}
          >
            {categories.length > 0
              ? categories.map((category, index) => (
                  <label
                    key={`${category.name}-${index}`}
                    htmlFor={`cat-${category.name}`}
                    className={`c-benefits-dropdown__item ${categoryFocusIndex === index ? "c-benefits-dropdown__item--focus" : ""}`}
                    role="option"
                    id={`c-benefits-dropdown__item--${index}`}
                    aria-selected={selectedCategories.includes(category)}
                  >
                    <svg viewBox="0 0 200 200" aria-hidden="true">
                      <use href={`${localize("svgSprite")}#${category.name}`} />
                    </svg>
                    {category.label}
                    <input
                      onChange={(event) =>
                        handleCategorySelect(event, category, index)
                      }
                      type="checkbox"
                      id={`cat-${category.name}`}
                      name={`cat-${category.name}`}
                      aria-hidden="true"
                      checked={selectedCategories.includes(category)}
                    />
                    <span className="c-benefits-dropdown__item-checkmark" />
                  </label>
                ))
              : null}
          </div>
        </div>

        {/* Location */}
        <div
          className={`s-benefits-filter__location ${loadingUserLocation ? "s-benefits-filter__location--loading" : ""}`}
          role="group"
          aria-label="Umkreissuche"
        >
          <div className="s-benefits-filter__location-input-wrapper">
            <div className="s-benefits-filter__location-loader"></div>
            <button
              type="button"
              onClick={getUserLocation}
              title="Deinen Standort verwenden"
              className="s-benefits-filter__location-button s-benefits-filter__location-button--location"
            >
              <span className="u-sr-only">
                {locationInputRef.current?.value === "Dein Standort"
                  ? "Standort aktiv"
                  : "Deinen Standort verwenden"}
              </span>
              <svg
                aria-hidden="true"
                className="map-pin"
                style={
                  locationInputRef.current?.value === "Dein Standort"
                    ? { fill: "#f39100" }
                    : {}
                }
                viewBox="0 0 200 200"
              >
                <use href={`${localize("svgSprite")}#map-pin`} />
              </svg>
            </button>

            <input
              type="text"
              name="location"
              aria-label="PLZ oder Ort"
              placeholder="PLZ oder Ort"
              ref={locationInputRef}
              readOnly={locationInputRef.current?.value === "Dein Standort"}
              onClick={() => handleLocationInputOnClick()}
              onChange={(event) => handleLocationInputOnChange(event)}
            />
            {locationValue !== "" ? (
              <button
                type="button"
                className="s-benefits-filter__location-button s-benefits-filter__location-button--reset"
                onClick={() => clearLocationInput()}
                aria-label="Umkreissuche zurücksetzen"
              >
                <svg viewBox="0 0 200 200" className="reset" aria-hidden="true">
                  <use href={`${localize("svgSprite")}#close`} />
                </svg>
              </button>
            ) : null}
          </div>
          <div
            className={`c-benefits-dropdown c-benefits-dropdown--gray ${distanceDropdownOpen ? "c-benefits-dropdown--open" : ""}`}
          >
            <div
              className="c-benefits-dropdown__header"
              id="c-benefits-dropdown__distance-header"
              onClick={toggleDistanceDropdown}
              onBlur={onDistanceDropdownBlur}
              onKeyDown={onDistanceDropdownKeyDown}
              tabIndex={0}
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={distanceDropdownOpen}
              aria-controls="c-benefits-distance-dropdown__select"
              aria-label="Umkreis auswählen"
              aria-activedescendant={
                distanceFocusIndex > -1
                  ? `c-benefits-distance-dropdown__item--${distanceFocusIndex}`
                  : ""
              }
            >
              <span>
                {selectedDistance > 1
                  ? `+ ${selectedDistance} km`
                  : "Ganzer Ort"}
              </span>
              <svg
                className="c-benefits-dropdown__chevron-down"
                viewBox="0 0 200 200"
              >
                <use href={`${localize("svgSprite")}#chevron-down`} />
              </svg>
            </div>
            <div
              className="c-benefits-dropdown__select"
              id="c-benefits-distance-dropdown__select"
              ref={distanceListRef}
              role="listbox"
              aria-multiselectable="false"
              aria-labelledby="c-benefits-dropdown__distance-header"
              tabIndex={-1}
            >
              {DistanceOptions.map((option, index) => (
                <div
                  key={option.value}
                  className={`c-benefits-dropdown__item ${distanceFocusIndex === index ? "c-benefits-dropdown__item--focus" : ""} ${selectedDistance === option.value ? "c-benefits-dropdown__item--selected" : ""}`}
                  id={`c-benefits-distance-dropdown__item--${index}`}
                  role="option"
                  aria-selected={selectedDistance === option.value}
                  onClick={() => handleDistanceSelect(option.value)}
                >
                  {option.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Find Button */}
        <button type="submit" className="c-button">
          Finden
        </button>
      </form>

      {/* Grid */}
      <div className="s-benefits-filter__grid">
        {loadingSearchResults
          ? [...Array(6)].map((value, index) => (
              <BenefitCardSkeleton key={`bcs-${index}`} />
            ))
          : searchResults.map((offer, index) => (
              <BenefitCard
                key={`${offer.title}-${index}`}
                baseUrl={baseUrl}
                offer={offer}
              />
            ))}
      </div>
      {showEnterSomethingMsg ? (
        <h3 style={{ margin: "0", padding: "0 0 60px", textAlign: "center" }}>
          Bitte wähle eine Kategorie aus oder gib eine PLZ oder einen Ort ein.
        </h3>
      ) : null}
      {(locationParam !== "" || categoriesParam.length > 0) &&
      searchResults.length === 0 ? (
        <h3 style={{ margin: "0", textAlign: "center" }}>
          Zu Deiner Suche wurden keine Vorteile gefunden, passe Deine Suche an
          und versuche es erneut.
        </h3>
      ) : null}
      {errorLoadingSearchResults ? (
        <h3 style={{ margin: "0", textAlign: "center" }}>
          Beim Laden der Suchergebnisse ist ein Fehler aufgetreten.
        </h3>
      ) : null}
    </div>
  );
};

BenefitsFilter.propTypes = {
  categories: PropTypes.array,
  baseUrl: PropTypes.string,
};

export default BenefitsFilter;
